import { useEffect, useState } from 'react';
import { Alert, Modal, Progress, message } from 'antd';
import DicomDropzone from './DicomDropzone';
import { useUploadStudy } from './hooks/useUploadStudy';
import styles from './UploadStudyModal.module.css';

interface UploadStudyModalProps {
  open: boolean;
  onClose: () => void;
  onUploadSuccess?: (uploadId?: string) => void;
}

export default function UploadStudyModal({
  open,
  onClose,
  onUploadSuccess,
}: UploadStudyModalProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [filesError, setFilesError] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const { upload, reset, state } = useUploadStudy();
  const isUploading =
    state.stage === 'preparing' ||
    state.stage === 'requesting-url' ||
    state.stage === 'uploading';

  // Reset state when the modal closes.
  useEffect(() => {
    if (!open) {
      setFiles([]);
      setFilesError(false);
      reset();
    }
  }, [open, reset]);

  // Clear the "files required" error as soon as files arrive.
  useEffect(() => {
    if (files.length > 0) setFilesError(false);
  }, [files]);

  const handleSubmit = async () => {
    if (files.length === 0) {
      setFilesError(true);
      messageApi.error('Please add at least one DICOM file or ZIP.');
      return;
    }
    try {
      const result = await upload({ files });
      messageApi.success('Upload sent — ingestion is processing.');
      onUploadSuccess?.(result?.uploadId);
      onClose();
    } catch {
      // error already surfaced via state.errorMessage
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        title="Upload study"
        open={open}
        onCancel={isUploading ? undefined : onClose}
        onOk={handleSubmit}
        okText="Upload study"
        okButtonProps={{
          loading: isUploading,
          disabled: files.length === 0,
        }}
        cancelButtonProps={{ disabled: isUploading }}
        width={560}
        destroyOnHidden
        centered
        maskClosable={!isUploading}
        wrapClassName={styles.belowHeaderWrap}
        styles={{
          body: {
            maxHeight: 'calc(100vh - var(--header-height, 64px) - 180px)',
            overflowY: 'auto',
            paddingRight: 20,
          },
        }}
      >
        <h4 className={styles.sectionLabel}>
          DICOM files <span className={styles.requiredMark}>*</span>
        </h4>
        <DicomDropzone
          files={files}
          onChange={setFiles}
          disabled={isUploading}
        />
        {filesError && (
          <div className={styles.filesErrorText}>
            Please add at least one DICOM file or ZIP.
          </div>
        )}

        {state.stage === 'uploading' && (
          <div className={styles.progressRow}>
            <Progress percent={state.progress} status="active" />
          </div>
        )}
        {state.stage === 'requesting-url' && (
          <div className={styles.progressRow}>
            <Progress percent={0} status="active" format={() => 'Preparing…'} />
          </div>
        )}
        {state.stage === 'error' && state.errorMessage && (
          <Alert
            type="error"
            showIcon
            className={styles.errorAlert}
            message="Upload failed"
            description={state.errorMessage}
          />
        )}
      </Modal>
    </>
  );
}
